import type { Suggestion } from '../types/suggestion.d';
import { SuggestionButtonManager } from './suggestion-button-manager';
import { I18n, getOrCreateI18n } from '@affine/i18n';
import { AcceptIcon, RejectIcon } from './suggestion-icon';

/**
 * 简化的建议UI管理器（去单例化版本 + MutationObserver）
 * 每个文档有独立的实例
 * 负责建议的样式应用和操作按钮管理
 */
export class SuggestionUIManager {
  private readonly _buttonManager: SuggestionButtonManager;
  private readonly _managerId: string;
  private static _stylesInjected = false;

  // ✅ 静态变量：全局 Accept/Reject All 事件监听器（所有实例共享）
  private static _globalAllOperationListenerSetup = false;
  private static _globalAllOperationListener: ((e: Event) => void) | null = null;

  // ✅ 版本控制
  private _currentBatchVersion: number = 0;

  // ✅ DOM 观察器
  private _domObserver: MutationObserver | null = null;
  private _editorContainer: HTMLElement | null = null;

  // ✅ Accept All / Reject All 按钮容器
  private _allOperationContainer: HTMLElement | null = null;

  // ✅ 语言变化监听器
  private _languageChangeHandler: (() => void) | null = null;

  // ✅ 待渲染任务队列（按 suggestion 粒度）
  private readonly _pendingTasks: Map<
    string,
    {
      suggestion: Suggestion;
      version: number;
      requiredBlocks: Set<string>;
      existingBlocks: Set<string>;
      retryCount: number;
      timeoutId?: NodeJS.Timeout;
    }
  > = new Map();

  constructor() {
    this._managerId = `UM-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    // ✅ 每个实例创建独立的 ButtonManager
    this._buttonManager = new SuggestionButtonManager();
    this._injectStyles();
    this._setupDOMObserver();
    this._setupAllOperationEventListener();
    this._setupLanguageChangeListener(); // ✅ 监听语言变化
    console.log('✅ SuggestionUIManager 实例创建:', this._managerId);
  }

  /**
   * ✅ 监听语言变化，自动更新按钮文本
   */
  private _setupLanguageChangeListener(): void {
    this._languageChangeHandler = () => {
      console.log(`🌍 [${this._managerId}] 语言变化，更新按钮文本`);
      // 如果按钮当前正在显示，重新创建它们以更新文本
      if (
        this._allOperationContainer &&
        this._allOperationContainer.parentNode
      ) {
        this._hideAllOperationButtons();
        this._showAllOperationButtons();
      }
    };

    // ✅ 直接使用 i18next 实例来监听语言变化
    const i18n = getOrCreateI18n();
    i18n.on('languageChanged', this._languageChangeHandler);
  }

  /**
   * ✅ 设置 Accept All / Reject All 事件监听器
   * 使用事件委托，类似 SuggestionButtonManager 的模式
   * 使用静态变量确保全局只注册一次，避免内存泄漏
   */
  private _setupAllOperationEventListener(): void {
    if (SuggestionUIManager._globalAllOperationListenerSetup) return;

    SuggestionUIManager._globalAllOperationListener = (e: Event) => {
      const target = e.target as HTMLElement;

      // ✅ 使用 closest() 向上查找匹配的按钮元素
      // 这样即使点击按钮内部的子元素（如 img）也能正确触发
      const acceptBtn = target.closest('.suggestion-accept-all-btn');
      const rejectBtn = target.closest('.suggestion-reject-all-btn');

      if (acceptBtn) {
        e.stopPropagation();
        e.preventDefault();

        console.log('🎯 Accept All 按钮点击');

        // ✅ 派发 custom event（类似单个 suggestion 的模式）
        document.dispatchEvent(
          new CustomEvent('suggestion-all-action', {
            detail: { action: 'accept-all' },
            bubbles: true,
          })
        );
      } else if (rejectBtn) {
        e.stopPropagation();
        e.preventDefault();

        console.log('🎯 Reject All 按钮点击');

        // ✅ 派发 custom event
        document.dispatchEvent(
          new CustomEvent('suggestion-all-action', {
            detail: { action: 'reject-all' },
            bubbles: true,
          })
        );
      }
    };

    document.addEventListener(
      'click',
      SuggestionUIManager._globalAllOperationListener,
      true
    );
    SuggestionUIManager._globalAllOperationListenerSetup = true;
    console.log('✅ 全局 Accept/Reject All 事件监听器已设置（所有实例共享）');
  }

  /**
   * ✅ 设置 DOM 观察器
   */
  private _setupDOMObserver(): void {
    // 1. 查找编辑器容器
    const findContainer = () => {
      return document.querySelector(
        '.affine-page-root-block-container'
      ) as HTMLElement;
    };

    this._editorContainer = findContainer();

    if (!this._editorContainer) {
      console.warn(`⚠️ [${this._managerId}] 编辑器容器未找到，稍后重试`);
      setTimeout(() => this._setupDOMObserver(), 500);
      return;
    }

    // 2. 创建 MutationObserver
    this._domObserver = new MutationObserver(mutations => {
      const addedBlockIds = new Set<string>();
      const removedBlockIds = new Set<string>();

      mutations.forEach(mutation => {
        // 处理新增节点
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;
            const blockId = element.dataset.blockId;

            if (blockId) {
              addedBlockIds.add(blockId);
            }

            // 递归查找子元素中的 blocks
            element.querySelectorAll('[data-block-id]').forEach(child => {
              const childBlockId = (child as HTMLElement).dataset.blockId;
              if (childBlockId) {
                addedBlockIds.add(childBlockId);
              }
            });
          }
        });

        // 处理删除节点
        mutation.removedNodes.forEach(node => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as HTMLElement;
            const blockId = element.dataset.blockId;

            if (blockId) {
              removedBlockIds.add(blockId);
            }

            // 递归查找子元素中的 blocks
            element.querySelectorAll('[data-block-id]').forEach(child => {
              const childBlockId = (child as HTMLElement).dataset.blockId;
              if (childBlockId) {
                removedBlockIds.add(childBlockId);
              }
            });
          }
        });
      });

      // 3. 处理待渲染任务
      if (addedBlockIds.size > 0) {
        this._handleBlocksAdded(addedBlockIds);
      }
      if (removedBlockIds.size > 0) {
        this._handleBlocksRemoved(removedBlockIds);
      }
    });

    // 3. 开始观察
    this._domObserver.observe(this._editorContainer, {
      childList: true,
      subtree: true,
    });

    console.log(`✅ [${this._managerId}] DOM 观察器已设置`);
  }

  /**
   * ✅ 处理 blocks 被添加
   */
  private _handleBlocksAdded(blockIds: Set<string>): void {
    // 检查是否有等待这些 blocks 的任务
    this._pendingTasks.forEach((task, suggestionId) => {
      blockIds.forEach(blockId => {
        if (task.requiredBlocks.has(blockId)) {
          task.existingBlocks.add(blockId);
        }
      });

      // 如果所有需要的 blocks 都已存在，执行渲染
      if (task.existingBlocks.size === task.requiredBlocks.size) {
        console.log(
          `✅ [${this._managerId}] Suggestion ${suggestionId} 的 blocks 已就绪，执行渲染`
        );

        // 清理超时定时器
        if (task.timeoutId) {
          clearTimeout(task.timeoutId);
        }

        // 执行渲染
        this._renderSuggestionImmediate(task.suggestion);

        // 移除任务
        this._pendingTasks.delete(suggestionId);
      }
    });
  }

  /**
   * ✅ 处理 blocks 被删除（可能是 accept/reject 导致的）
   */
  private _handleBlocksRemoved(blockIds: Set<string>): void {
    // 更新待渲染任务（移除已被删除的 blocks）
    this._pendingTasks.forEach((task, suggestionId) => {
      blockIds.forEach(blockId => {
        if (task.requiredBlocks.has(blockId)) {
          // 这个 block 被删除了，从需求中移除
          task.requiredBlocks.delete(blockId);
          task.existingBlocks.delete(blockId);
        }
      });

      // 如果没有需要的 blocks 了，取消任务
      if (task.requiredBlocks.size === 0) {
        console.log(
          `⚠️ [${this._managerId}] Suggestion ${suggestionId} 的所有 blocks 都被删除，取消渲染`
        );

        if (task.timeoutId) {
          clearTimeout(task.timeoutId);
        }

        this._pendingTasks.delete(suggestionId);
      }
    });
  }

  /**
   * 🔑 批量同步 suggestions 到 UI（入口方法，由 ySuggestionInfo observe 触发）
   */
  syncSuggestions(suggestions: Suggestion[]): void {
    const batchVersion = ++this._currentBatchVersion;

    console.log(
      `🔄 [${this._managerId}] V${batchVersion} 同步 ${suggestions.length} 个 suggestions 到 UI`
    );

    // ✅ 1. 取消上一批次的所有待渲染任务
    this._cancelAllPendingTasks();

    // ✅ 2. 更新 Accept All / Reject All 按钮显示状态
    const hasPending = suggestions.some(s => s.status === 'pending');
    this._updateAllOperationButtons(hasPending);

    const currentSuggestionIds = new Set(suggestions.map(s => s.id));

    // ✅ 2. 清理不存在的 suggestions UI（同步）
    const allSuggestionElements = document.querySelectorAll(
      '[data-suggestion-id]'
    );
    allSuggestionElements.forEach(element => {
      const suggestionId = (element as HTMLElement).dataset.suggestionId;
      if (suggestionId && !currentSuggestionIds.has(suggestionId)) {
        console.log(
          `🧹 [${this._managerId}] 清理不存在的 suggestion UI: ${suggestionId}`
        );
        this._clearSuggestionStylesImmediate(suggestionId);
      }
    });

    // ✅ 3. 渲染/更新 suggestions
    suggestions.forEach(suggestion => {
      if (suggestion.status === 'pending') {
        const existing = document.querySelector(
          `[data-suggestion-id="${suggestion.id}"]`
        );

        if (!existing) {
          this._scheduleSuggestionRender(suggestion, batchVersion);
        }
      } else {
        // 非 pending 状态的应该被清理
        // console.log(
        //   `🧹 [${this._managerId}] 清理非 pending 状态: ${suggestion.id} (${suggestion.status})`
        // );
        this._clearSuggestionStylesImmediate(suggestion.id);
      }
    });
  }

  /**
   * ✅ 取消所有待渲染任务
   */
  private _cancelAllPendingTasks(): void {
    this._pendingTasks.forEach((task, suggestionId) => {
      if (task.timeoutId) {
        clearTimeout(task.timeoutId);
      }
      console.log(`⚠️ [${this._managerId}] 取消待渲染任务: ${suggestionId}`);
    });

    this._pendingTasks.clear();
  }

  /**
   * ✅ 调度 suggestion 渲染（智能判断是否需要等待）
   */
  private _scheduleSuggestionRender(
    suggestion: Suggestion,
    batchVersion: number
  ): void {
    // 收集需要的 blockIds
    const requiredBlocks = new Set<string>([
      ...suggestion.targetBlockIds,
      ...(suggestion.newBlockIds || []),
    ]);

    // 检查哪些 blocks 已经存在
    const existingBlocks = new Set<string>();
    requiredBlocks.forEach(blockId => {
      if (this._getBlockElement(blockId)) {
        existingBlocks.add(blockId);
      }
    });

    // 如果所有 blocks 都已存在，立即渲染
    if (existingBlocks.size === requiredBlocks.size) {
      this._renderSuggestionImmediate(suggestion);
      return;
    }

    // 设置超时机制（2 秒后强制渲染或放弃）
    const timeoutId = setTimeout(() => {
      const task = this._pendingTasks.get(suggestion.id);
      if (task && task.version === batchVersion) {
        console.warn(
          `⚠️ [${this._managerId}] Suggestion ${suggestion.id} 等待超时，尝试部分渲染`
        );

        // 即使部分 blocks 缺失，也尝试渲染（容错）
        this._renderSuggestionImmediate(suggestion);
        this._pendingTasks.delete(suggestion.id);
      }
    }, 2000);

    // 加入队列
    this._pendingTasks.set(suggestion.id, {
      suggestion,
      version: batchVersion,
      requiredBlocks,
      existingBlocks,
      retryCount: 0,
      timeoutId,
    });
  }

  /**
   * ✅ 立即渲染 suggestion（同步操作）
   */
  private _renderSuggestionImmediate(suggestion: Suggestion): void {
    switch (suggestion.type) {
      case 'insert':
        this._renderInsertSuggestion(suggestion);
        break;
      case 'delete':
        this._renderDeleteSuggestion(suggestion);
        break;
      case 'replace':
        this._renderReplaceSuggestion(suggestion);
        break;
      default:
        console.warn(`Unknown suggestion type: ${(suggestion as any).type}`);
    }

    // 创建按钮
    const targetElement = this._getBlockElement(
      suggestion.newBlockIds?.[0] || suggestion.targetBlockIds[0]
    );

    if (targetElement) {
      this._buttonManager.createButton(suggestion.id, targetElement);
    } else {
      console.warn(
        `⚠️ [${this._managerId}] 无法为 suggestion ${suggestion.id} 创建按钮：目标元素不存在`
      );
    }
  }

  /**
   * ✅ 立即清理 suggestion 样式（同步）
   */
  private _clearSuggestionStylesImmediate(suggestionId: string): void {
    const elements = document.querySelectorAll(
      `[data-suggestion-id="${suggestionId}"]`
    );

    elements.forEach(element => {
      element.classList.remove(
        'suggestion-replace-target',
        'suggestion-replace-content',
        'suggestion-old-background'
      );
      delete (element as HTMLElement).dataset.suggestionId;
    });

    // ✅ 移除按钮
    this._buttonManager.removeButton(suggestionId);
  }

  /**
   * 渲染建议UI（向后兼容的公共接口）
   */
  renderSuggestion(suggestion: Suggestion): void {
    this._renderSuggestionImmediate(suggestion);
  }

  /**
   * 清理建议UI（向后兼容的公共接口）
   */
  clearSuggestion(suggestionId: string): void {
    // 取消待渲染任务
    const task = this._pendingTasks.get(suggestionId);
    if (task) {
      if (task.timeoutId) {
        clearTimeout(task.timeoutId);
      }
      this._pendingTasks.delete(suggestionId);
    }

    // 清理 UI
    this._clearSuggestionStylesImmediate(suggestionId);
  }

  /**
   * ✅ 更新 Accept All / Reject All 按钮
   */
  private _updateAllOperationButtons(hasPending: boolean): void {
    if (hasPending) {
      this._showAllOperationButtons();
    } else {
      this._hideAllOperationButtons();
    }
  }

  /**
   * ✅ 显示 Accept All / Reject All 按钮
   */
  private _showAllOperationButtons(): void {
    // 如果已经显示，不重复创建
    if (
      this._allOperationContainer &&
      document.body.contains(this._allOperationContainer)
    ) {
      return;
    }

    const anchor = document.querySelector(
      '[data-lit-react-wrapper="PageEditor"]'
    ) as HTMLElement;
    if (!anchor) {
      console.warn(`⚠️ [${this._managerId}] 未找到挂载点`);
      return;
    }

    // 创建容器
    this._allOperationContainer = document.createElement('div');
    this._allOperationContainer.className =
      'suggestion-all-operation-container';
    this._allOperationContainer.id = 'suggestion-operation-intro-container';

    const operationText = document.createElement('span');
    operationText.className = 'suggestion-all-operation-text';
    operationText.textContent = I18n.t(
      'com.affine.settings.editorSettings.page.suggestion.operation-text'
    );

    this._allOperationContainer.appendChild(operationText);

    // Accept All 按钮
    const acceptBtn = document.createElement('button');
    acceptBtn.className =
      'suggestion-all-btn suggestion-accept-all-btn suggestion-all-operation-accept';
    acceptBtn.style.display = 'flex';
    acceptBtn.style.alignItems = 'center';
    acceptBtn.style.justifyContent = 'center';
    // const acceptImg = document.createElement('img');
    // acceptImg.src = '/imgs/editor/suggestion_accept.png';
    // acceptImg.alt = 'Accept';
    // acceptImg.style.width = '24px';
    // acceptImg.style.height = '24px';
    acceptBtn.insertAdjacentHTML('beforeend', AcceptIcon);

    // ✅ 使用 I18n 对象（非 React 环境）
    acceptBtn.appendChild(
      document.createTextNode(
        I18n.t('com.affine.settings.editorSettings.page.suggestion.apply-all')
      )
    );

    // Reject All 按钮
    const rejectBtn = document.createElement('button');
    rejectBtn.className =
      'suggestion-all-btn suggestion-reject-all-btn suggestion-all-operation-reject';
    rejectBtn.style.display = 'flex';
    rejectBtn.style.alignItems = 'center';
    rejectBtn.style.justifyContent = 'center';
    // const rejectImg = document.createElement('img');
    // rejectImg.src = '/imgs/editor/suggestion_reject.png';
    // rejectImg.alt = 'Reject';
    // rejectImg.style.width = '24px';
    // rejectImg.style.height = '24px';
    // rejectBtn.appendChild(rejectImg);
    rejectBtn.insertAdjacentHTML('beforeend', RejectIcon);

    // ✅ 使用 I18n 对象（非 React 环境）
    rejectBtn.appendChild(
      document.createTextNode(
        I18n.t('com.affine.settings.editorSettings.page.suggestion.dismiss-all')
      )
    );

    const applybtnWrapper = document.createElement('div');
    applybtnWrapper.className = 'suggestion-all-btn-wrapper';
    applybtnWrapper.appendChild(rejectBtn);
    applybtnWrapper.appendChild(acceptBtn);
    this._allOperationContainer.appendChild(applybtnWrapper);

    this._attachDomCenterBottom(anchor, this._allOperationContainer);
    anchor.appendChild(this._allOperationContainer);

    console.log(`✅ [${this._managerId}] Accept All / Reject All 按钮已显示`);
  }

  /**
   * ✅ 隐藏 Accept All / Reject All 按钮
   */
  private _hideAllOperationButtons(): void {
    if (this._allOperationContainer && this._allOperationContainer.parentNode) {
      this._allOperationContainer.parentNode.removeChild(
        this._allOperationContainer
      );
      this._allOperationContainer = null;
      console.log(`🧹 [${this._managerId}] Accept All / Reject All 按钮已隐藏`);
    }
  }

  private _attachDomCenterBottom(domA: HTMLElement, dom2: HTMLElement) {
    if (!domA || !dom2) return;

    function updatePosition() {
      const rect = domA.getBoundingClientRect();
      const centerX = rect.width / 2; // domA 横向中心点
      dom2.style.position = 'fixed';
      dom2.style.bottom = '16px';
      dom2.style.left = `${centerX}px`;
      dom2.style.transform = 'translateX(-50%)'; // 保证 dom2 中心对齐
    }

    // 初始化定位
    updatePosition();

    // 窗口大小变化时也要更新
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition);

    // 监听 domA 尺寸变化
    const resizeObserver = new ResizeObserver(updatePosition);
    resizeObserver.observe(domA);

    // 返回一个销毁方法，方便解绑监听
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition);
    };
  }

  /**
   * ✅ 清理所有 suggestions
   */
  clearAllSuggestions(): void {
    console.log(`🧹 [${this._managerId}] 清理所有 suggestions`);

    // 1. 取消所有待渲染任务
    this._cancelAllPendingTasks();

    // 2. 版本号递增（使旧任务失效）
    this._currentBatchVersion++;

    // 3. ✅ 隐藏 Accept All / Reject All 按钮
    this._hideAllOperationButtons();

    // 4. 同步清理 DOM
    const allSuggestionElements = document.querySelectorAll(
      '[data-suggestion-id]'
    );
    allSuggestionElements.forEach(element => {
      const suggestionId = (element as HTMLElement).dataset.suggestionId;
      if (suggestionId) {
        this._clearSuggestionStylesImmediate(suggestionId);
      }
    });
  }

  /**
   * ✅ 销毁 UI Manager
   */
  dispose(): void {
    console.log(`🧹 [${this._managerId}] SuggestionUIManager 销毁`);

    // 1. 断开 MutationObserver
    if (this._domObserver) {
      this._domObserver.disconnect();
      this._domObserver = null;
    }

    // 2. 取消语言变化监听
    if (this._languageChangeHandler) {
      const i18n = getOrCreateI18n();
      i18n.off('languageChanged', this._languageChangeHandler);
      this._languageChangeHandler = null;
    }

    // 3. 清理所有任务和 UI
    this.clearAllSuggestions();

    // 4. 销毁按钮管理器
    this._buttonManager.destroy();

    // 注意：全局 Accept/Reject All 事件监听器不在这里清理，
    // 因为它是静态的，由所有实例共享。只在应用完全卸载时才调用 SuggestionUIManager.cleanup()
  }

  /**
   * ✅ 静态清理方法（仅在应用完全卸载时调用）
   */
  static cleanup(): void {
    if (SuggestionUIManager._globalAllOperationListener) {
      document.removeEventListener(
        'click',
        SuggestionUIManager._globalAllOperationListener,
        true
      );
      SuggestionUIManager._globalAllOperationListener = null;
      SuggestionUIManager._globalAllOperationListenerSetup = false;
      console.log('🧹 全局 Accept/Reject All 事件监听器已清理');
    }
  }

  private _renderDeleteSuggestion(suggestion: Suggestion): void {
    suggestion.targetBlockIds.forEach(blockId => {
      const element = this._getBlockElement(blockId);
      if (element) {
        (element as HTMLElement).dataset.suggestionId = suggestion.id;

        element.classList.add('suggestion-replace-target');
      }
    });
  }

  private _renderInsertSuggestion(suggestion: Suggestion): void {
    if (suggestion.newBlockIds) {
      suggestion.newBlockIds.forEach(blockId => {
        const element = this._getBlockElement(blockId);
        if (element) {
          (element as HTMLElement).dataset.suggestionId = suggestion.id;
          element.classList.add('suggestion-replace-content');
        }
      });
    }
  }

  private _renderReplaceSuggestion(suggestion: Suggestion): void {
    // 目标块使用删除样式
    suggestion.targetBlockIds.forEach(blockId => {
      const element = this._getBlockElement(blockId);
      if (element) {
        (element as HTMLElement).dataset.suggestionId = suggestion.id;
        element.classList.add('suggestion-replace-target');
      }
    });

    // 新块使用插入样式
    if (suggestion.newBlockIds) {
      suggestion.newBlockIds.forEach(blockId => {
        const element = this._getBlockElement(blockId);
        if (element) {
          (element as HTMLElement).dataset.suggestionId = suggestion.id;
          element.classList.add('suggestion-replace-content');
        }
      });
    }
  }

  private _getBlockElement(blockId: string): Element | null {
    return document.querySelector(`[data-block-id="${blockId}"]`);
  }

  private _injectStyles(): void {
    if (SuggestionUIManager._stylesInjected) return;

    const styleId = 'affine-suggestion-styles-new';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .suggestion-button-container {
          position: relative;
          top: 10px;
          left: 0px;
          right: 0;
          z-index: 1000;
      }
      .suggestion-button-container {
          display: flex;
          gap: 40px;
          justify-content: end;
      }
      .suggestion-button-container-wrapper {
        display: flex;
        z-index: 99999;
        width: 60px;
        justify-content: space-around;
        background: rgba(0, 0, 0, 0.3);
        border-radius: 20px;
        padding: 0px 4px;
        align-items: center;
        color: #fff;
        border: 1px solid rgba(255, 255, 255, 0.1);
      }

      .suggestion-button-division {
        width: 0.33px;
        height: 12px;
        background-color: #fff;
        opacity: 0.2;
      }
      .suggestion-button-apply {
        color: #fff;
        font-size: 20px;
        width: 20px;
        font-weight: 400;
        height: 20px;
        // padding: 3px 5px;
        align-items: center;
        display: flex;
        justify-content: center;
      }

      .suggestion-old-background {
        opacity: 0.4;
      }

      .suggestion-old-background [data-v-text="true"],
      .suggestion-old-background [data-v-text="true"] * {
        /* 标准删除线 */
        text-decoration-line: line-through;
        /* 可选：更可控的线宽与颜色 */
        text-decoration-thickness: 1.2px;
        text-decoration-color: currentColor;
        /* 避免线条被字体细节跳过 */
        text-decoration-skip-ink: none;
      }
      .suggestion-new-background {
        
      }
      .suggestion-new-background > div:first-of-type {
        // box-shadow: inset 0 0 0 9999px rgba(0, 188, 255, 0.1);
        background-color: rgba(0, 188, 255, 0.1);
      }
      
      .suggestion-all-operation-container {
        width: fit-content;
        align-items: center;
        display: flex;
        font-size: 14px;
        z-index: 99999;
        justify-content: space-around;
        color: #fff;
        height: 44px;
        opacity: 1;
        border-radius: 24px;
        border-width: 1px;
        padding-top: 8px;
        padding-right: 8px;
        padding-bottom: 8px;
        padding-left: 16px;
        background: #00000099;
        border: 1px solid #FFFFFF14;
        gap: 24px;
      }
      .suggestion-all-operation-text {
        display: inline-block;
        white-space: nowrap;
      }
      .suggestion-all-btn-wrapper {
        display: flex;
        opacity: 1;
        gap: 8px;
      }

      .suggestion-all-btn {
        white-space: nowrap;
        font-size: 12px;
        height: 28px;
        opacity: 1;
        border-radius: 18px;
        border-width: 1px;
        padding-top: 2px;
        padding-right: 14px;
        padding-bottom: 2px;
        padding-left: 6px;
        background: rgba(255, 255, 255, 0.04);
        border: 1px solid rgba(255, 255, 255, 0.08);
      }
      .suggestion-accept-all-btn {
        background: rgba(73, 255, 130, 0.08);
        border: 1px solid rgba(73, 255, 130, 0.14)
      }

      .clickable {
        cursor: pointer;
      }

      .disabled-button {
        cursor: not-allowed;
        opacity: 0.5;
      }

      .suggestion-replace-content {
        // background-color:rgba(85, 0, 255, 0.1);
        color: rgba(0, 155, 114, 1);
      }

      .suggestion-replace-target *{
        /* 标准删除线 */
        text-decoration-line: line-through;
        /* 可选：更可控的线宽与颜色 */
        text-decoration-thickness: 1.2px;
        text-decoration-color: currentColor;
        /* 避免线条被字体细节跳过 */
        text-decoration-skip-ink: none;
        opacity: 0.9;
      }

    `;

    document.head.append(style);
    SuggestionUIManager._stylesInjected = true;
  }
}
